// Enhanced steganography utility with XOR encryption and better distribution

function stringToKey(keyString) {
  // Convert string key to repeating byte array
  const keyBytes = [];
  for (let i = 0; i < keyString.length; i++) {
    keyBytes.push(keyString.charCodeAt(i));
  }
  return keyBytes.length > 0 ? keyBytes : [0]; // Default key if empty
}

function xorEncrypt(data, key) {
  const keyBytes = stringToKey(key);
  const result = [];
  for (let i = 0; i < data.length; i++) {
    result.push(data[i] ^ keyBytes[i % keyBytes.length]);
  }
  return result;
}

function xorDecrypt(data, key) {
  // XOR decryption is the same as encryption
  return xorEncrypt(data, key);
}

export function validateImage(file) {
  const maxSize = 1048576; // 1MB in bytes
  const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
  
  if (!file) {
    throw new Error('Please select an image file');
  }
  
  if (file.size > maxSize) {
    throw new Error(`Image must be less than 1MB. Current size: ${(file.size / 1048576).toFixed(2)}MB`);
  }
  
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Only PNG, JPEG, and GIF formats are supported');
  }
  
  return true;
}

export function validateMessage(message) {
  const maxLength = 128;
  
  if (!message || message.trim().length === 0) {
    throw new Error('Please enter a message to encode');
  }
  
  if (message.length > maxLength) {
    throw new Error(`Message must be ${maxLength} characters or less. Current length: ${message.length}`);
  }
  
  return true;
}

export function validateKey(key) {
  if (!key || key.trim().length === 0) {
    throw new Error('Please enter an encryption key');
  }
  
  if (key.length < 4) {
    throw new Error('Key must be at least 4 characters long');
  }
  
  return true;
}

export function checkImageCapacity(imageData, message) {
  // Calculate available pixels for data storage
  const totalPixels = imageData.width * imageData.height;
  const availableBits = totalPixels * 3; // RGB channels
  
  // Calculate required bits (message + metadata)
  const messageBits = message.length * 8;
  const metadataBits = 32; // 4 bytes for message length
  const terminatorBits = 8; // Null terminator
  const requiredBits = messageBits + metadataBits + terminatorBits;
  
  if (requiredBits > availableBits) {
    throw new Error(`Image too small for message. Required: ${requiredBits} bits, Available: ${availableBits} bits`);
  }
  
  return true;
}

export function encodeMessage(imageData, message, key) {
  try {
    validateMessage(message);
    validateKey(key);
    checkImageCapacity(imageData, message);
    
    const data = imageData.data;
    
    // Convert message to bytes and encrypt
    const messageBytes = [];
    for (let i = 0; i < message.length; i++) {
      messageBytes.push(message.charCodeAt(i));
    }
    
    const encryptedBytes = xorEncrypt(messageBytes, key);
    
    // Create binary data with length prefix
    const messageLength = encryptedBytes.length;
    const lengthBinary = messageLength.toString(2).padStart(32, '0');
    
    // Convert encrypted message to binary
    const messageBinary = encryptedBytes
      .map(byte => byte.toString(2).padStart(8, '0'))
      .join('');
    
    // Add terminator
    const terminatorBinary = '00000000';
    
    // Combine all binary data
    const fullBinary = lengthBinary + messageBinary + terminatorBinary;
    
    // Encode into image using LSB across RGB channels
    let binaryIndex = 0;
    for (let i = 0; i < data.length && binaryIndex < fullBinary.length; i += 4) {
      // Skip alpha channel, use RGB channels
      for (let channel = 0; channel < 3 && binaryIndex < fullBinary.length; channel++) {
        const pixelIndex = i + channel;
        data[pixelIndex] = (data[pixelIndex] & 0xFE) | parseInt(fullBinary[binaryIndex]);
        binaryIndex++;
      }
    }
    
    return imageData;
  } catch (error) {
    throw new Error(`Encoding failed: ${error.message}`);
  }
}

export function decodeMessage(imageData, key) {
  try {
    validateKey(key);
    
    const data = imageData.data;
    let binaryData = '';
    
    // Extract binary data from RGB channels
    for (let i = 0; i < data.length; i += 4) {
      for (let channel = 0; channel < 3; channel++) {
        const pixelIndex = i + channel;
        binaryData += (data[pixelIndex] & 1).toString();
      }
    }
    
    // Extract message length (first 32 bits)
    const lengthBinary = binaryData.slice(0, 32);
    const messageLength = parseInt(lengthBinary, 2);
    
    if (messageLength <= 0 || messageLength > 128) {
      throw new Error('No valid message found or message corrupted');
    }
    
    // Extract encrypted message
    const messageBinary = binaryData.slice(32, 32 + (messageLength * 8));
    
    // Convert binary to encrypted bytes
    const encryptedBytes = [];
    for (let i = 0; i < messageBinary.length; i += 8) {
      const byte = messageBinary.slice(i, i + 8);
      if (byte.length === 8) {
        encryptedBytes.push(parseInt(byte, 2));
      }
    }
    
    // Decrypt the message
    const decryptedBytes = xorDecrypt(encryptedBytes, key);
    
    // Convert bytes back to string
    let message = '';
    for (let i = 0; i < decryptedBytes.length; i++) {
      message += String.fromCharCode(decryptedBytes[i]);
    }
    
    return message;
  } catch (error) {
    throw new Error(`Decoding failed: ${error.message}`);
  }
}
